import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, get, isUnavailable, post, type SelfUser } from '../services/api';

interface AuthApi {
  /** `undefined` while the first answer is on its way, `null` when nobody is signed in. */
  user: SelfUser | null | undefined;
  /**
   * Set when the last check could not reach the server. `user` is then `null` on a first visit, but that
   * means "unknown", not "signed out": pages say the server is down instead of sending a person to sign in.
   */
  unavailable: ApiError | null;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (input: { username: string; password: string; email?: string; country?: string }) => Promise<void>;
  signOut: (onSuccess?: () => void) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SelfUser | null | undefined>(undefined);
  const [unavailable, setUnavailable] = useState<ApiError | null>(null);

  const refresh = useCallback(async () => {
    try {
      setUser(await get<SelfUser>('/user'));
      setUnavailable(null);
    } catch (error) {
      if (isUnavailable(error) || (error instanceof ApiError && error.status !== 401)) {
        // Keep whoever was signed in: a dropped connection is not a sign-out.
        setUnavailable(error as ApiError);
        setUser((previous) => previous ?? null);
      } else {
        setUnavailable(null);
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    const expire = () => {
      setUser(null);
      setUnavailable(null);
    };
    window.addEventListener('athenaeum-session-expired', expire);
    return () => window.removeEventListener('athenaeum-session-expired', expire);
  }, [refresh]);

  const value = useMemo<AuthApi>(
    () => ({
      user,
      unavailable,
      refresh,
      signIn: async (username, password) => {
        const reply = await post<{ user: SelfUser }>('/auth/login', {
          username,
          password,
        });
        setUser(reply.user);
        setUnavailable(null);
      },
      signUp: async (input) => {
        const reply = await post<{ user: SelfUser }>('/auth/register', input);
        setUser(reply.user);
        setUnavailable(null);
      },
      signOut: async (onSuccess) => {
        await post('/auth/logout');
        // Keep session and router updates in the same transition; protected pages must not redirect
        // using the previous location while sign-out is taking the person to the public home.
        startTransition(() => {
          onSuccess?.();
          setUser(null);
          setUnavailable(null);
        });
      },
    }),
    [user, unavailable, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
